import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  createConsent,
  DEFAULT_LIMITS,
  type ConsentLimits,
} from '../services/consent';

export default function ConsentConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const oauthSessionId = searchParams.get('session') || '';
  const aspsp = searchParams.get('aspsp') || '';

  const [label, setLabel] = useState('');
  const [limits, setLimits] = useState<ConsentLimits>({ ...DEFAULT_LIMITS });
  const [validUntilDays, setValidUntilDays] = useState(90);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Missing session means the user navigated here directly
  if (!oauthSessionId) {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Create Consent</h1>
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
          <p className="text-sm text-yellow-800">
            No OAuth session found. Please start by selecting a bank on the{' '}
            <a href="/banks" className="font-medium underline">
              Banks page
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + validUntilDays);

      const response = await createConsent({
        oauth_session_id: oauthSessionId,
        label: label.trim() || undefined,
        valid_until: validUntil.toISOString(),
        limits,
        account_selection: 'all',
        terms_accepted: termsAccepted,
      });

      if (response.authorization_url) {
        // Bank requires SCA - redirect to bank's consent authorization page
        window.location.href = response.authorization_url;
      } else {
        // No SCA needed - consent is already authorized
        navigate(`/consent/success?consent_id=${encodeURIComponent(response.consent_id)}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create consent');
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Create Consent</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure your PSD2 consent for{' '}
          <span className="font-medium text-gray-700">{aspsp || 'the bank'}</span>
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Consent Label */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Consent Label</h2>
          <div>
            <label htmlFor="label" className="block text-sm text-gray-600 mb-1">
              A name for this consent (optional)
            </label>
            <input
              id="label"
              type="text"
              maxLength={100}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={`e.g., ${aspsp || 'Bank'} — daily account`}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>
        </div>

        {/* Account Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Account Access</h2>
          <p className="text-sm text-gray-500">
            This consent will grant access to all accounts at the bank.
          </p>
          <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-sm">
            All accounts (balances + transactions)
          </div>
        </div>

        {/* Validity Period */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Validity Period</h2>
          <div>
            <label htmlFor="validDays" className="block text-sm text-gray-600 mb-1">
              Consent valid for (days)
            </label>
            <select
              id="validDays"
              value={validUntilDays}
              onChange={(e) => setValidUntilDays(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            >
              <option value={30}>30 days</option>
              <option value={90}>90 days (recommended)</option>
              <option value={180}>180 days</option>
              <option value={365}>365 days (maximum)</option>
            </select>
          </div>
        </div>

        {/* Consent Limits */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-900">Payment Limits</h2>

          <div>
            <label htmlFor="maxAmount" className="block text-sm text-gray-600 mb-1">
              Max amount per payment ({limits.currency})
            </label>
            <input
              id="maxAmount"
              type="number"
              min={1}
              max={1000000}
              step={0.01}
              value={limits.max_amount_per_payment}
              onChange={(e) =>
                setLimits((l) => ({
                  ...l,
                  max_amount_per_payment: Number(e.target.value),
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="maxDaily" className="block text-sm text-gray-600 mb-1">
                Max payments/day
              </label>
              <input
                id="maxDaily"
                type="number"
                min={1}
                max={1000}
                value={limits.max_payments_per_day}
                onChange={(e) =>
                  setLimits((l) => ({
                    ...l,
                    max_payments_per_day: Number(e.target.value),
                  }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
            <div>
              <label htmlFor="maxMonthly" className="block text-sm text-gray-600 mb-1">
                Max payments/month
              </label>
              <input
                id="maxMonthly"
                type="number"
                min={1}
                max={10000}
                value={limits.max_payments_per_month}
                onChange={(e) =>
                  setLimits((l) => ({
                    ...l,
                    max_payments_per_month: Number(e.target.value),
                  }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label htmlFor="currency" className="block text-sm text-gray-600 mb-1">
              Currency
            </label>
            <select
              id="currency"
              value={limits.currency}
              onChange={(e) =>
                setLimits((l) => ({ ...l, currency: e.target.value }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            >
              <option value="EUR">EUR</option>
              <option value="SEK">SEK</option>
              <option value="DKK">DKK</option>
              <option value="NOK">NOK</option>
            </select>
          </div>
        </div>

        {/* Concordium Costs (stub) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-2">
          <h2 className="text-base font-semibold text-gray-900">Blockchain Registration</h2>
          <p className="text-sm text-gray-500">
            Your consent will be registered on the Concordium blockchain for transparency and auditability.
          </p>
          <p className="text-sm text-gray-400 italic">
            Estimated cost: ~0.01 CCD (stub - cost calculation coming in a future update)
          </p>
        </div>

        {/* Terms & Conditions (stub) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              I accept the{' '}
              <span className="font-medium text-primary-600">
                Terms and Conditions
              </span>{' '}
              for this PSD2 consent.
              <span className="block text-xs text-gray-400 mt-1">
                (Placeholder - full T&C text will be added in a future update)
              </span>
            </span>
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !termsAccepted}
          className="w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Creating consent...' : 'Create Consent'}
        </button>
      </form>
    </div>
  );
}
